import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { TranslateService } from '@ngx-translate/core';
import { DeviceTokenRestService, DeviceTokenUpsertPayload } from '../../api/services/device-token.rest.service';
import { StorageService } from './storage.service';
import { TokenDataProviderService } from './token-data-provider.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {
  private static readonly STORAGE_TOKEN = 'push.fcmToken';
  private static readonly STORAGE_TOKEN_ID = 'push.deviceTokenId';

  public readonly notificationReceived$ = new Subject<PushNotificationSchema>();
  public readonly notificationAction$ = new Subject<ActionPerformed>();

  private listenersRegistered = false;
  private initialized = false;
  private latestSettingsPushEnabled = true;
  private isAuthenticated = false;
  private activeUserId: number | null = null;
  private lastPushSettingsKey: string | null = null;
  private lastUpsertKey: string | null = null;
  private upsertInFlightKey: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly deviceTokenRS: DeviceTokenRestService,
    private readonly storage: StorageService,
    private readonly tokenDPS: TokenDataProviderService,
  ) {
  }

  public init(): void {
    if (!this.isNativePlatform() || this.initialized) {
      return;
    }
    this.initialized = true;

    if (!this.listenersRegistered) {
      this.registerListeners();
      this.listenersRegistered = true;
    }

    this.tokenDPS.user$.subscribe((user) => {
      this.isAuthenticated = !!user?.id;
      this.activeUserId = user?.id || null;
      if (!this.isAuthenticated) {
        this.lastPushSettingsKey = null;
        this.lastUpsertKey = null;
        return;
      }

      const enabled = user?.settings?.notification?.push !== false;
      this.latestSettingsPushEnabled = enabled;
      const settingsKey = `${user.id}:${enabled}:${this.translate.currentLang || 'en-US'}`;
      if (this.lastPushSettingsKey === settingsKey) {
        return;
      }
      this.lastPushSettingsKey = settingsKey;
      if (!enabled) {
        void this.disablePush();
        return;
      }

      void this.enablePushIfAllowed();

      const storedToken = this.storage.getItem(PushNotificationsService.STORAGE_TOKEN);
      if (storedToken) {
        void this.upsertToken(storedToken);
      }
    });

  }

  public async syncWithSettings(isEnabled: boolean): Promise<void> {
    if (!this.isAuthenticated) {
      return;
    }

    this.latestSettingsPushEnabled = isEnabled;
    if (isEnabled) {
      await this.enablePushIfAllowed();
      return;
    }

    await this.disablePush();
  }

  private async enablePushIfAllowed(): Promise<void> {
    if (!this.isAuthenticated || !this.latestSettingsPushEnabled) {
      return;
    }

    if (!this.listenersRegistered) {
      this.registerListeners();
      this.listenersRegistered = true;
    }

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') {
      return;
    }

    // Reactivate an existing token immediately after enabling push in settings.
    const storedToken = this.storage.getItem(PushNotificationsService.STORAGE_TOKEN);
    if (storedToken) {
      await this.upsertToken(storedToken);
    }

    await PushNotifications.register();

    if (Capacitor.getPlatform() === 'ios' && this.isFirebaseMessagingAvailable()) {
      await this.syncIosFcmToken();
    }
  }

  private async disablePush(): Promise<void> {
    if (!this.isAuthenticated) {
      return;
    }

    const tokenId = this.storage.getItem(PushNotificationsService.STORAGE_TOKEN_ID);
    if (tokenId) {
      this.deviceTokenRS.disable(tokenId).subscribe();
    }
  }

  private registerListeners(): void {
    PushNotifications.addListener('registration', (token: Token) => {
      void this.upsertToken(token.value);
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.warn('Push registration error', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      this.notificationReceived$.next(notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      this.notificationAction$.next(action);
      this.handleNavigation(action.notification?.data || {});
    });

    if (this.isFirebaseMessagingAvailable()) {
      FirebaseMessaging.addListener('tokenReceived', (event) => {
        void this.upsertToken(event.token);
      });
    }
  }

  private async upsertToken(token: string): Promise<void> {
    if (!this.isAuthenticated || !this.latestSettingsPushEnabled) {
      return;
    }

    const platform = Capacitor.getPlatform();
    if (platform !== 'ios' && platform !== 'android') {
      return;
    }

    const locale = this.translate.currentLang || 'en-US';
    const effectiveToken = await this.resolveEffectiveToken(platform, token);
    if (!effectiveToken) {
      return;
    }
    const upsertKey = `${this.activeUserId}:${platform}:${locale}:${effectiveToken}`;
    if (this.lastUpsertKey === upsertKey || this.upsertInFlightKey === upsertKey) {
      return;
    }
    this.upsertInFlightKey = upsertKey;
    try {
      const appInfo = await App.getInfo();
      const deviceInfo = await Device.getId();
      const deviceId = (deviceInfo as any).identifier || (deviceInfo as any).uuid || '';
      const payload: DeviceTokenUpsertPayload = {
        token: effectiveToken,
        platform,
        locale,
        appVersion: appInfo.version,
        deviceId: deviceId || undefined,
      };

      this.storage.setItem(PushNotificationsService.STORAGE_TOKEN, effectiveToken);
      this.deviceTokenRS.upsert(payload).subscribe((deviceToken) => {
        this.upsertInFlightKey = null;
        if (deviceToken?.id) {
          this.storage.setItem(PushNotificationsService.STORAGE_TOKEN_ID, deviceToken.id);
        }
        this.lastUpsertKey = upsertKey;
      }, () => {
        this.upsertInFlightKey = null;
      });
    } catch {
      this.upsertInFlightKey = null;
    }
  }

  private async syncIosFcmToken(): Promise<void> {
    const token = await this.resolveEffectiveToken('ios');
    if (!token) {
      return;
    }

    await this.upsertToken(token);
  }

  private async resolveEffectiveToken(
    platform: 'ios' | 'android',
    fallbackToken?: string
  ): Promise<string | null> {
    if (platform === 'ios' && this.isFirebaseMessagingAvailable()) {
      try {
        const result = await FirebaseMessaging.getToken();
        if (result?.token) {
          return result.token;
        }
      } catch (error) {
        console.warn('Unable to fetch iOS FCM token', error);
      }
    }

    return fallbackToken || null;
  }

  private isFirebaseMessagingAvailable(): boolean {
    return Capacitor.isPluginAvailable('FirebaseMessaging');
  }

  private handleNavigation(data: Record<string, any>): void {
    const deepLink = data['deepLink'] || data['deeplink'];
    if (deepLink) {
      this.router.navigateByUrl(deepLink);
      return;
    }

    const type = data['type'] || data['category'];
    switch (type) {
      case 'chat':
        if (data['threadId']) {
          this.router.navigate(['/chat', data['threadId']]);
        }
        break;
      case 'event':
        if (data['eventId']) {
          this.router.navigate(['/events', data['eventId']]);
        }
        break;
      case 'marketplace':
        if (data['adId']) {
          this.router.navigate(['/marketplace/offer', data['adId']]);
        }
        break;
      default:
        break;
    }
  }

  private isNativePlatform(): boolean {
    return isPlatformBrowser(this.platformId) && Capacitor.getPlatform() !== 'web';
  }
}
